import { useMemo, useState } from 'react';

import {
  ChakraFlex,
  ChakraText,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Group,
  Reset,
} from '@equip.health/ui';

import {
  Recurrence,
  getRecurrenceDisplay,
  getRecurrenceFrequency,
} from '~/lib/util/calendar.util';
import { getPreferredFullName } from '~/lib/util/patient.util';

import AttendeeList from '../AttendeeList';
import Participant from '../Participant';

const { primary } = EquipUIFireflyV2Theme.colors;

const { h6, paragraph, paragraphBold } = EquipUITheme.typography;

const TOP_LEVEL_ID = 'group-class-event-summary';

const linkTextStyles = {
  ...paragraphBold,
  color: primary[100],
  cursor: 'pointer',
  lineHeight: '16.8px',
  variant: 'ghost',
};

interface GroupClassEventSummaryProps {
  groupClassEventDetails: GroupClassEventDetails;
  isHostShow?: boolean;
}

const GroupClassEventSummary = ({
  groupClassEventDetails,
  isHostShow = true,
}: GroupClassEventSummaryProps) => {
  const { attendees, declinedUsers, frequency, hosts, startTime, title } =
    groupClassEventDetails || {};

  const [hideAttendees, setHideAttendees] = useState<boolean>(true);

  const toggleViewAttendees = (): void => {
    setHideAttendees(!hideAttendees);
  };

  const attendeesCount = (attendees ?? []).length;

  const declinedUsersCount = (declinedUsers ?? []).length;

  const recurrenceFrequency = getRecurrenceFrequency(frequency);

  const attendeesMapper: Record<string, GroupClassEventAttendeeExtended[]> =
    useMemo(() => {
      const attendeesToUpdate = (attendees ?? []).map(
        (attendee: GroupClassEventAttendee) => ({
          ...attendee,
          isAttendee: true,
        }),
      );
      const declinedUsersToUpdate = (declinedUsers ?? []).map(
        (attendee: GroupClassEventAttendee) => ({
          ...attendee,
          isAttendee: false,
        }),
      );
      const attendeesToDisplay = attendeesToUpdate.concat(
        declinedUsersToUpdate,
      );
      const half = Math.ceil(attendeesToDisplay.length / 2);
      return {
        left: attendeesToDisplay.slice(0, half),
        right: attendeesToDisplay.slice(half),
      };
    }, [attendees, declinedUsers]);

  const hasAttendees = attendeesCount > 0 || declinedUsersCount > 0;
  const isSingleAttendee = attendeesCount === 1;
  const isShowAttendeesDetailsCTA = attendees?.[0] && attendees[0].firstName;

  return (
    <ChakraFlex align="center" width="100%">
      <ChakraFlex direction="column" width="100%">
        <ChakraText {...h6} id={`${TOP_LEVEL_ID}__title`} paddingBottom="12px">
          {title}
        </ChakraText>
        {isHostShow &&
          (hosts ?? []).map((host: GroupClassEventAttendee) => (
            <Participant
              id={host.externalId}
              isChecked
              isHost
              isReadOnly
              key={host.externalId}
              primaryText={getPreferredFullName(
                host?.chosenName,
                host.firstName,
                host.lastName,
              )}
              secondaryText={host?.providerType}
            />
          ))}
        {recurrenceFrequency !== Recurrence.DOES_NOT_REPEAT && (
          <ChakraFlex align="center" paddingBottom="12px">
            <Reset fill={primary[100]} height="16px" width="16px" />
            <ChakraText
              {...paragraph}
              color="neutral.secondary"
              id={`${TOP_LEVEL_ID}__recurrence`}
              lineHeight="16.8px"
              paddingLeft="12px"
            >
              {getRecurrenceDisplay(
                startTime ??
                  (groupClassEventDetails as unknown as ScheduleItemBase)
                    .startDateTime,
                recurrenceFrequency,
                null,
              )}
            </ChakraText>
          </ChakraFlex>
        )}
        {hasAttendees && (
          <ChakraFlex align="center" paddingBottom="5px">
            <Group fill={primary[100]} height="16px" width="16px" />
            <ChakraText
              {...paragraph}
              color="neutral.secondary"
              id={`${TOP_LEVEL_ID}__attendee-count`}
              lineHeight="16.8px"
              paddingLeft="12px"
            >
              {`${attendeesCount} attendee${isSingleAttendee ? '' : 's'}`}
            </ChakraText>
            {isShowAttendeesDetailsCTA && (
              <ChakraText
                {...linkTextStyles}
                id={`${TOP_LEVEL_ID}__show-hide-attendees`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleViewAttendees();
                }}
              >
                &nbsp;&nbsp;{hideAttendees ? 'Show' : 'Hide'}
              </ChakraText>
            )}
          </ChakraFlex>
        )}
        {!hideAttendees && (
          <ChakraFlex>
            <AttendeeList attendees={attendeesMapper.left} />
            <AttendeeList attendees={attendeesMapper.right} />
          </ChakraFlex>
        )}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default GroupClassEventSummary;
